export type Item = Record<string, any> & { id: string };

// 请求超时：接口卡住时不至于让界面一直转圈。
const REQUEST_TIMEOUT_MS = Number((import.meta as any).env?.VITE_API_TIMEOUT_MS ?? 20_000);
// 会话过期（401）时通知上层：清本地登录态并跳登录页，而不是把 401 当普通报错抛给用户。
let onUnauthorized: (() => void) | undefined;
export function setUnauthorizedHandler(handler: () => void) { onUnauthorized = handler; }

// 登录相关接口的 401 是「账号密码错误」，不能当成会话过期处理。
const AUTH_PATHS = /^\/(login|login\/code|login\/totp|auth\/bot)/;

/** 单次请求的可选项：timeoutMs 用来给"批量外呼"这类慢接口单独放宽超时（默认 20 秒） */
export interface RequestOptions { timeoutMs?: number }

async function call<T>(path: string, init?: RequestInit, options?: RequestOptions): Promise<T> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), options?.timeoutMs ?? REQUEST_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      credentials: "include",
      headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
      ...init,
      signal: controller.signal
    });
  } catch (reason) {
    if (controller.signal.aborted) throw new Error("请求超时，请检查网络后重试", { cause: reason });
    throw reason;
  } finally {
    window.clearTimeout(timer);
  }
  if (response.status === 401 && !AUTH_PATHS.test(path)) {
    onUnauthorized?.();
    throw new Error("登录已过期，请重新登录");
  }
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message ?? `请求失败：${response.status}`);
  }
  if (response.status === 204) return undefined as T;
  return response.json();
}

// 轻量 GET 缓存：同一渲染周期内的重复请求（如各视图反复拉 /catalog）合并，写操作后整体失效。
const GET_TTL_MS = 10_000;
const getCache = new Map<string, { value: unknown; at: number }>();
function clearGetCache() { getCache.clear(); }

async function cachedGet<T>(path: string): Promise<T> {
  const hit = getCache.get(path);
  if (hit && Date.now() - hit.at < GET_TTL_MS) return hit.value as T;
  const value = await call<T>(path);
  getCache.set(path, { value, at: Date.now() });
  return value;
}

export const api = {
  get: <T>(path: string) => cachedGet<T>(path),
  tryGet: async <T>(path: string, fallback: T) => {
    try { return await cachedGet<T>(path); } catch { return fallback; }
  },
  post: <T>(path: string, value: unknown, options?: RequestOptions) => { clearGetCache(); return call<T>(path, { method: "POST", body: JSON.stringify(value) }, options); },
  put: <T>(path: string, value: unknown) => { clearGetCache(); return call<T>(path, { method: "PUT", body: JSON.stringify(value) }); },
  patch: <T>(path: string, value: unknown) => { clearGetCache(); return call<T>(path, { method: "PATCH", body: JSON.stringify(value) }); },
  del: <T>(path: string, value?: unknown) => { clearGetCache(); return call<T>(path, { method: "DELETE", body: JSON.stringify(value ?? {}) }); }
};
